import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE, getToken } from "../../lib/api";
import { COLORS } from "../../lib/brand";
import {
  Plus, Pencil, Trash2, Save, X, ArrowLeft, AlertTriangle, CheckCircle2, Upload,
  Mail, Phone, MapPin, FileText, Download, DollarSign, BadgeCheck, Send
} from "lucide-react";
import { toast } from "sonner";

const CREDENTIAL_FIELDS = [
  { key: "w2", label: "W-2 Form" },
  { key: "signing_contract", label: "Signing Contract" },
  { key: "eo_insurance", label: "E&O Insurance" },
  { key: "llc_certification", label: "LLC Certification" },
];

const STATUS_COLORS = {
  active: { bg: "#FBF8F1", fg: "#800000" },
  inactive: { bg: "#F3F3F3", fg: "#5C5C5C" },
  pending: { bg: "#FFF4DC", fg: "#B8941F" },
};

// ============================================================
// List page
// ============================================================
export function AdminNotaries() {
  const [notaries, setNotaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/notaries");
      setNotaries(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function createNotary(payload) {
    try {
      await api.post("/admin/notaries", payload);
      toast.success("Notary added");
      setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const totalSignings = notaries.reduce((s, n) => s + (n.signings_count || 0), 0);
  const totalRevenue = notaries.reduce((s, n) => s + (n.revenue_total || 0), 0);
  const notariesMissingDocs = notaries.filter((n) => (n.missing_credentials || []).length > 0).length;

  return (
    <div className="space-y-6" data-testid="admin-notaries-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1A1A1A]">Notary Back-End</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Manage the notaries working under India's signing service.</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium text-white"
          style={{ background: COLORS.maroon }}
          data-testid="add-notary-btn"
        >
          <Plus size={14} /> Add Notary
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile icon={<BadgeCheck />} label="Active Notaries" value={notaries.filter((n) => n.status === "active").length} accent={COLORS.maroon} />
        <StatTile icon={<FileText />} label="Total Signings" value={totalSignings} accent={COLORS.gold} />
        <StatTile icon={<AlertTriangle />} label="Missing Documents" value={notariesMissingDocs} accent={notariesMissingDocs > 0 ? "#B8941F" : "#1A1A1A"} />
      </div>

      {creating && (
        <div className="bg-white rounded-xl border border-[#800000] shadow-sm p-5">
          <h3 className="font-medium text-[#1A1A1A] mb-3">Add a new notary</h3>
          <NotaryForm onSubmit={createNotary} onCancel={() => setCreating(false)} />
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[#5C5C5C]">Loading...</div>
        ) : notaries.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">No notaries yet. Click "Add Notary" to start your network.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FBF8F1] text-[#5C5C5C] uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Location</th>
                <th className="text-left px-5 py-3">Signings</th>
                <th className="text-left px-5 py-3">Revenue</th>
                <th className="text-left px-5 py-3">Credentials</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D5]">
              {notaries.map((n) => {
                const missing = n.missing_credentials || [];
                const expiring = n.expiring_credentials || [];
                const statusColor = STATUS_COLORS[n.status] || STATUS_COLORS.active;
                return (
                  <tr key={n.id} className="hover:bg-[#FBF8F1]/50" data-testid={`notary-row-${n.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-[#1A1A1A]">{n.first_name} {n.last_name}</div>
                      <div className="text-xs text-[#5C5C5C]">{n.email}</div>
                    </td>
                    <td className="px-5 py-4 text-[#1A1A1A]">{n.location || "—"}</td>
                    <td className="px-5 py-4 text-[#1A1A1A] font-medium">{n.signings_count || 0}</td>
                    <td className="px-5 py-4 text-[#1A1A1A] font-medium">${(n.revenue_total || 0).toLocaleString()}</td>
                    <td className="px-5 py-4">
                      {missing.length === 0 && expiring.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#FBF8F1] text-[#800000]">
                          <CheckCircle2 size={12} /> Complete
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full" style={{ background: "#FFF4DC", color: "#B8941F" }}>
                          <AlertTriangle size={12} /> {missing.length} missing · {expiring.length} expired
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-xs px-2.5 py-1 rounded-full capitalize" style={{ background: statusColor.bg, color: statusColor.fg }}>
                        {n.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Link to={`/admin/notaries/${n.id}`} className="text-[#800000] hover:text-[#5C0000] font-medium" data-testid={`open-notary-${n.id}`}>Open →</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Detail page
// ============================================================
export function AdminNotaryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notary, setNotary] = useState(null);
  const [signings, setSignings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [n, s, r] = await Promise.all([
        api.get(`/admin/notaries/${id}`),
        api.get(`/admin/signings?notary_id=${id}`),
        api.get(`/admin/notaries/${id}/document-requests`),
      ]);
      setNotary(n.data);
      setSignings(s.data || []);
      setRequests(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function saveEdits(payload) {
    try {
      const r = await api.patch(`/admin/notaries/${id}`, payload);
      setNotary(r.data);
      toast.success("Saved");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function patchCredential(field, patch) {
    const next = {
      ...notary.credentials,
      [field]: { ...(notary.credentials?.[field] || {}), ...patch },
    };
    try {
      const r = await api.patch(`/admin/notaries/${id}`, { credentials: next });
      setNotary(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deleteNotary() {
    if (!confirm("Remove this notary from the network?")) return;
    try {
      await api.delete(`/admin/notaries/${id}`);
      toast.success("Removed");
      navigate("/admin/notaries");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (loading) return <div className="text-sm text-[#5C5C5C]">Loading...</div>;
  if (!notary) return <div className="text-sm text-[#5C5C5C]">Notary not found.</div>;

  return (
    <div className="space-y-6" data-testid="admin-notary-detail">
      <button onClick={() => navigate(-1)} className="text-sm text-[#5C5C5C] hover:text-[#800000] flex items-center gap-1">
        <ArrowLeft size={14} /> Back
      </button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A1A]">{notary.first_name} {notary.last_name}</h1>
          <div className="text-sm text-[#5C5C5C] mt-1">{notary.email} · {notary.phone || "no phone"}</div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-[#5C5C5C]">{notary.signings_count || 0} signings · ${(notary.revenue_total || 0).toLocaleString()} revenue</span>
            <span className="px-2 py-0.5 rounded-full capitalize" style={{ background: STATUS_COLORS[notary.status]?.bg, color: STATUS_COLORS[notary.status]?.fg }}>{notary.status}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <button onClick={() => setEditing(false)} className="text-sm border border-[#E8E2D5] px-3 py-2 rounded-md flex items-center gap-1 hover:border-[#800000]" data-testid="cancel-notary-edit">
              <X size={14} /> Cancel
            </button>
          ) : (
            <button onClick={() => setEditing(true)} className="text-sm text-white px-3 py-2 rounded-md flex items-center gap-1" style={{ background: COLORS.maroon }} data-testid="edit-notary-btn">
              <Pencil size={14} /> Edit
            </button>
          )}
          <button onClick={deleteNotary} className="text-sm border border-[#E8E2D5] px-3 py-2 rounded-md text-[#5C5C5C] hover:text-[#800000] hover:border-[#800000]" data-testid="delete-notary-btn">
            <Trash2 size={14} />
          </button>
        </div>
      </header>

      {editing && (
        <div className="bg-white rounded-xl border border-[#800000] shadow-sm p-5">
          <NotaryForm initial={notary} onSubmit={saveEdits} onCancel={() => setEditing(false)} submitLabel="Save Changes" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: contact + commission */}
        <div className="space-y-6">
          <Card title="Contact & Commission">
            <DetailRow icon={<Mail size={14} />} value={notary.email} />
            {notary.phone && <DetailRow icon={<Phone size={14} />} value={notary.phone} />}
            {notary.location && <DetailRow icon={<MapPin size={14} />} value={notary.location} />}
            <DetailRow label="Commission State" value={notary.commission_state || "—"} />
            <DetailRow label="Commission Expires" value={notary.commission_expires || "—"} expired={notary.commission_expires && notary.commission_expires <= new Date().toISOString().slice(0, 10)} />
            {notary.notes && <DetailRow label="Notes" value={notary.notes} />}
          </Card>

          <DocumentRequestCard notary={notary} requests={requests} onSent={load} />
        </div>

        {/* Center: credentials */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Credential Tracker">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CREDENTIAL_FIELDS.map((c) => (
                <CredentialBlock
                  key={c.key}
                  field={c.key}
                  label={c.label}
                  credential={notary.credentials?.[c.key] || {}}
                  onChange={(patch) => patchCredential(c.key, patch)}
                />
              ))}
            </div>
          </Card>

          <SigningsLogCard notaryId={notary.id} signings={signings} onChange={load} />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Subcomponents
// ============================================================
function StatTile({ icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E2D5] p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-lg flex items-center justify-center text-[#800000]" style={{ background: "#FBF8F1" }}>{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#5C5C5C]">{label}</div>
        <div className="text-2xl font-semibold mt-0.5" style={{ color: accent }}>{value}</div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm">
      <div className="px-5 py-4 border-b border-[#E8E2D5]"><h3 className="font-medium text-[#1A1A1A]">{title}</h3></div>
      <div className="px-5 py-4 space-y-2">{children}</div>
    </section>
  );
}

function DetailRow({ icon, label, value, expired }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {icon && <span className="text-[#5C5C5C] mt-0.5">{icon}</span>}
      {label && <span className="text-[#5C5C5C] w-36 shrink-0">{label}</span>}
      <span className={`break-words ${expired ? "text-[#B8941F]" : "text-[#1A1A1A]"}`}>{value}{expired ? " (expired)" : ""}</span>
    </div>
  );
}

const inputCls = "w-full px-2.5 py-1.5 border border-[#E8E2D5] rounded text-sm focus:outline-none focus:border-[#800000] focus:ring-1 focus:ring-[#800000]";

function NotaryForm({ initial = {}, onSubmit, onCancel, submitLabel = "Add Notary" }) {
  const [form, setForm] = useState({
    first_name: initial.first_name || "",
    last_name: initial.last_name || "",
    email: initial.email || "",
    phone: initial.phone || "",
    location: initial.location || "",
    commission_state: initial.commission_state || "",
    commission_expires: initial.commission_expires || "",
    status: initial.status || "active",
    notes: initial.notes || "",
  });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    await onSubmit(form);
    setBusy(false);
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="notary-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FieldLabel label="First Name *"><input required className={inputCls} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="notary-first-name" /></FieldLabel>
        <FieldLabel label="Last Name *"><input required className={inputCls} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="notary-last-name" /></FieldLabel>
        <FieldLabel label="Email *"><input required type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="notary-email" /></FieldLabel>
        <FieldLabel label="Phone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></FieldLabel>
        <FieldLabel label="Location"><input className={inputCls} placeholder="Nashville, TN" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></FieldLabel>
        <FieldLabel label="Commission State"><input className={inputCls} value={form.commission_state} onChange={(e) => setForm({ ...form, commission_state: e.target.value })} /></FieldLabel>
        <FieldLabel label="Commission Expires"><input type="date" className={inputCls} value={form.commission_expires || ""} onChange={(e) => setForm({ ...form, commission_expires: e.target.value })} /></FieldLabel>
        <FieldLabel label="Status">
          <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
          </select>
        </FieldLabel>
      </div>
      <FieldLabel label="Notes"><textarea className={`${inputCls} min-h-[60px]`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FieldLabel>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="px-4 py-2 rounded-md text-sm text-white" style={{ background: COLORS.maroon }} data-testid="notary-submit-btn">
          {busy ? "Saving..." : submitLabel}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md text-sm text-[#5C5C5C] hover:text-[#800000]">Cancel</button>
      </div>
    </form>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C] block mb-1">{label}</span>
      {children}
    </label>
  );
}

function CredentialBlock({ field, label, credential, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = credential.on_file;
  const today = new Date().toISOString().slice(0, 10);
  const expired = credential.expires_on && credential.expires_on <= today;

  async function upload(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("purpose", `notary_${field}`);
      fd.append("label", `${label} — ${f.name}`);
      const { data } = await api.post("/admin/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ on_file: true, file_id: data.id });
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function download() {
    if (!credential.file_id) return;
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/files/${credential.file_id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = label; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download");
    }
  }

  return (
    <div className={`border rounded-lg p-3 ${onFile ? (expired ? "border-[#B8941F] bg-[#FFFCF4]" : "border-[#800000]/30 bg-[#FBF8F1]/50") : "border-dashed border-[#E8E2D5]"}`} data-testid={`credential-${field}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-sm text-[#1A1A1A]">{label}</div>
        {onFile ? (
          expired ? (
            <span className="text-[10px] uppercase tracking-wider text-[#B8941F] flex items-center gap-1"><AlertTriangle size={11} /> Expired</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-[#800000] flex items-center gap-1"><CheckCircle2 size={11} /> On File</span>
          )
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Missing</span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-white cursor-pointer" style={{ background: COLORS.maroon }}>
          <Upload size={12} /> {uploading ? "Uploading..." : onFile ? "Replace" : "Upload"}
          <input ref={fileRef} type="file" hidden onChange={upload} disabled={uploading} data-testid={`credential-upload-${field}`} />
        </label>
        {credential.file_id && (
          <button onClick={download} className="text-xs text-[#800000] hover:text-[#5C0000] flex items-center gap-1" data-testid={`credential-download-${field}`}>
            <Download size={12} /> Download
          </button>
        )}
        {onFile && (
          <button onClick={() => { if (confirm(`Remove ${label} from file?`)) onChange({ on_file: false, file_id: null, expires_on: null }); }} className="text-xs text-[#5C5C5C] hover:text-[#800000]" data-testid={`credential-remove-${field}`}>
            <X size={12} />
          </button>
        )}
      </div>

      <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C] block">Expires</label>
      <input
        type="date"
        value={credential.expires_on || ""}
        onChange={(e) => onChange({ expires_on: e.target.value || null })}
        className="w-full px-2 py-1 border border-[#E8E2D5] rounded text-xs"
        data-testid={`credential-expires-${field}`}
      />
    </div>
  );
}

function DocumentRequestCard({ notary, requests, onSent }) {
  const [selected, setSelected] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const missing = notary.missing_credentials || [];

  useEffect(() => { setSelected(missing); /* eslint-disable-next-line */ }, [notary.id]);

  function toggle(field) {
    setSelected((s) => s.includes(field) ? s.filter((x) => x !== field) : [...s, field]);
  }

  async function send() {
    if (selected.length === 0) return toast.error("Pick at least one document");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/notaries/${notary.id}/request-documents`, {
        requested_fields: selected,
        custom_message: msg || null,
      });
      if (data.sent_via_ghl) {
        toast.success("Request sent via GHL workflow");
      } else {
        toast.success("Request logged (GHL webhook not configured)");
      }
      setMsg("");
      onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm" data-testid="document-request-card">
      <div className="px-5 py-4 border-b border-[#E8E2D5]">
        <h3 className="font-medium text-[#1A1A1A]">Request Missing Documents</h3>
        <p className="text-xs text-[#5C5C5C] mt-0.5">Sends a notary-document-request event to GHL → triggers email to {notary.email}.</p>
      </div>
      <div className="px-5 py-4 space-y-3">
        {CREDENTIAL_FIELDS.map((c) => (
          <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={selected.includes(c.key)}
              onChange={() => toggle(c.key)}
              className="accent-[#800000]"
              data-testid={`request-doc-${c.key}`}
            />
            <span className={`${missing.includes(c.key) ? "text-[#B8941F] font-medium" : "text-[#1A1A1A]"}`}>{c.label}</span>
            {missing.includes(c.key) && <span className="text-[10px] uppercase text-[#B8941F]">Missing</span>}
          </label>
        ))}

        <textarea
          placeholder="Optional custom message"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          className={`${inputCls} min-h-[60px]`}
          data-testid="request-doc-message"
        />

        <button
          onClick={send}
          disabled={busy || selected.length === 0}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm text-white disabled:opacity-60"
          style={{ background: COLORS.maroon }}
          data-testid="send-document-request-btn"
        >
          <Send size={14} /> {busy ? "Sending..." : "Send Request"}
        </button>

        {requests.length > 0 && (
          <div className="pt-3 border-t border-[#E8E2D5]">
            <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mb-2">Recent requests</div>
            <ul className="space-y-1.5 text-xs">
              {requests.slice(0, 5).map((r) => (
                <li key={r.id} className="text-[#5C5C5C]">
                  {new Date(r.sent_at).toLocaleDateString()} · {(r.requested_fields || []).join(", ")} · {r.sent_via_ghl ? <span className="text-[#800000]">sent ✓</span> : <span className="text-[#B8941F]">logged only</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function SigningsLogCard({ notaryId, signings, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ signing_date: new Date().toISOString().slice(0, 10), signing_type: "Loan signing", revenue: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const total = signings.length;
  const totalRev = signings.reduce((s, x) => s + (x.revenue || 0), 0);

  async function add(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/signings", { notary_id: notaryId, ...form, revenue: Number(form.revenue) || 0 });
      toast.success("Signing logged");
      setForm({ signing_date: new Date().toISOString().slice(0, 10), signing_type: "Loan signing", revenue: "", notes: "" });
      setAdding(false);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!confirm("Delete this signing log?")) return;
    try {
      await api.delete(`/admin/signings/${id}`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm" data-testid="signings-log-card">
      <div className="px-5 py-4 border-b border-[#E8E2D5] flex items-center justify-between">
        <div>
          <h3 className="font-medium text-[#1A1A1A]">Signings Log</h3>
          <p className="text-xs text-[#5C5C5C] mt-0.5">{total} signings · ${totalRev.toLocaleString()} total revenue</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="text-sm text-white px-3 py-1.5 rounded-md flex items-center gap-1" style={{ background: COLORS.maroon }} data-testid="add-signing-btn">
          <Plus size={13} /> Log Signing
        </button>
      </div>

      {adding && (
        <form onSubmit={add} className="p-5 border-b border-[#E8E2D5] grid grid-cols-1 md:grid-cols-4 gap-2">
          <input type="date" required value={form.signing_date} onChange={(e) => setForm({ ...form, signing_date: e.target.value })} className={inputCls} data-testid="signing-date-input" />
          <input required placeholder="Type (Loan, RON, Apostille…)" value={form.signing_type} onChange={(e) => setForm({ ...form, signing_type: e.target.value })} className={inputCls} data-testid="signing-type-input" />
          <input type="number" step="0.01" placeholder="Revenue $" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} className={inputCls} data-testid="signing-revenue-input" />
          <button disabled={busy} className="px-3 py-1.5 rounded-md text-sm text-white" style={{ background: COLORS.maroon }} data-testid="signing-submit-btn">{busy ? "..." : "Save"}</button>
        </form>
      )}

      {signings.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-[#5C5C5C]">No signings logged yet.</div>
      ) : (
        <ul className="divide-y divide-[#E8E2D5]">
          {signings.map((s) => (
            <li key={s.id} className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-[#1A1A1A]">{s.signing_type}</div>
                <div className="text-xs text-[#5C5C5C]">{s.signing_date}{s.notes ? ` · ${s.notes}` : ""}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-[#800000]">${(s.revenue || 0).toLocaleString()}</span>
                <button onClick={() => remove(s.id)} className="text-[#5C5C5C] hover:text-[#800000]"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
